import { useState, useEffect } from "react";
import { T, ALL_LABELS } from "../lib/i18n";
import { dbLoadSession } from "../lib/db";
import { Dialog, QuestionText, QuestionImages } from "../components/common";

// ─────────────────────────────────────────
//  FÖRHANDSVISNING AV FRÅGORNA (lärardashboarden)
// ─────────────────────────────────────────
// Alla frågor på en gång, i standardordningen — samma numrering som
// Frågeanalys och appeals — utan att behöva fälla ut dem en och en.
//
// Facit är dolt från början: dashboarden visas ofta på projektor, och rätt
// svar ska inte hamna på duken av misstag.
//
// Dashboarden läser in sessionen en gång när den öppnas. Förhandsvisningen
// hämtar den på nytt, så en fråga som rättats i Redigera syns direkt.
function QuestionPreview({ session: initial, onClose, lang, G, css }) {
  const t = T[lang];
  const [session, setSession] = useState(initial);
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (initial?.dashId) {
      dbLoadSession(initial.dashId).then(s => { if (!cancelled && s) setSession(s); }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [initial?.dashId]);

  const numQ = session.numQ || 0;
  const LABELS = ALL_LABELS.slice(0, session.numOptions || 4);
  const questions = session.questions || [];
  const answerKey = session.answerKey || [];
  const shuffled = !!(session.config?.randomize_questions || session.config?.randomize_options);
  const optionTexts = q => LABELS.map((lbl, oi) => ({ lbl, oi, text: q?.options?.[lbl]?.trim() || "" }));
  const hasContent = q => !!(q?.text?.trim() || q?.images?.length || optionTexts(q).some(o => o.text));
  const paperOnly = !questions.some(hasContent);

  const headerBtn = active => ({
    background: active ? G.gold + "18" : "transparent",
    border: `1px solid ${active ? G.gold + "66" : G.border}`,
    borderRadius: 8, color: active ? G.gold : G.text, fontSize: 13, fontWeight: 500,
    padding: "6px 14px", cursor: "pointer", whiteSpace: "nowrap", fontFamily: "'DM Sans',sans-serif",
  });

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <Dialog onClose={onClose} label={t.questionsTitle}
        style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 16, width: "100%", maxWidth: 760, maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 50px rgba(0,0,0,0.3)" }}>

        {/* Sidhuvud — står kvar medan frågorna rullar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "16px 20px", borderBottom: `1px solid ${G.border}` }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ ...css.heading, fontSize: 18, color: G.text }}>{t.questionsTitle} · {numQ}</div>
            {session.sessionTitle && (
              <div style={{ fontSize: 13, color: G.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{session.sessionTitle}</div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={() => setShowKey(p => !p)} aria-pressed={showKey} style={headerBtn(showKey)}>
              {showKey ? t.hideKey : t.showKey}
            </button>
            <button onClick={onClose} aria-label={lang === "sv" ? "Stäng" : "Close"}
              style={{ background: "none", border: "none", color: G.muted, fontSize: 20, cursor: "pointer", padding: "2px 6px", lineHeight: 1 }}>✕</button>
          </div>
        </div>

        <div style={{ overflowY: "auto", padding: "4px 20px 20px" }}>
          {shuffled && (
            <div style={{ fontSize: 13, color: G.muted, lineHeight: 1.6, background: G.bg, border: `1px solid ${G.border}`, borderRadius: 10, padding: "10px 14px", margin: "14px 0 4px" }}>
              {t.previewShuffleNote}
            </div>
          )}
          {paperOnly && (
            <div style={{ fontSize: 14, color: G.muted, lineHeight: 1.6, margin: "14px 0 4px" }}>{t.previewNoQuestions}</div>
          )}

          {Array.from({ length: numQ }, (_, qi) => {
            const q = questions[qi] || {};
            const correct = answerKey[qi];
            const opts = optionTexts(q);
            const hasOptions = opts.some(o => o.text);
            return (
              <section key={qi} aria-label={`Q${qi + 1}`} style={{ borderTop: qi > 0 || shuffled || paperOnly ? `1px solid ${G.border}` : "none", padding: "18px 0", marginTop: qi === 0 ? 4 : 0 }}>
                <div style={{ ...css.heading, fontSize: 17, color: G.gold, marginBottom: 8 }}>Q{qi + 1}</div>

                {q.text?.trim() && <QuestionText text={q.text} style={{ fontSize: 15, color: G.text, lineHeight: 1.65, marginBottom: 10 }} />}
                <QuestionImages images={q.images} G={G} lang={lang} maxHeight={320} />
                {!paperOnly && !hasContent(q) && (
                  <div style={{ fontSize: 13, color: G.muted, fontStyle: "italic", marginBottom: 8 }}>{t.previewPaperOnly}</div>
                )}

                {hasOptions ? (
                  <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                    {opts.filter(o => o.text).map(o => {
                      const isCorrect = showKey && o.oi === correct;
                      return (
                        <li key={o.lbl} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "6px 10px", borderRadius: 8, border: `1px solid ${isCorrect ? G.correct + "77" : "transparent"}`, background: isCorrect ? G.correct + "14" : "transparent" }}>
                          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 14, color: isCorrect ? G.correct : G.gold, fontWeight: 600, minWidth: 18 }}>{o.lbl}</span>
                          <span style={{ fontSize: 14, color: G.text, lineHeight: 1.6, flex: 1 }}>{o.text}</span>
                          {/* Text och bock, inte bara färg (WCAG 1.4.1) */}
                          {isCorrect && <span style={{ fontSize: 12, fontWeight: 600, color: G.correct, whiteSpace: "nowrap" }}>✓ {t.correctOption}</span>}
                        </li>
                      );
                    })}
                  </ol>
                ) : showKey && Number.isInteger(correct) && LABELS[correct] && (
                  <div style={{ fontSize: 14, fontWeight: 600, color: G.correct }}>✓ {t.correctOption}: {LABELS[correct]}</div>
                )}

                {showKey && q.rationale?.trim() && (
                  <div style={{ background: G.gold + "12", border: `1px solid ${G.gold}33`, borderRadius: 10, padding: "10px 14px", marginTop: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: G.gold, marginBottom: 4 }}>{t.rationale}</div>
                    <QuestionText text={q.rationale} style={{ fontSize: 14, color: G.text, lineHeight: 1.6 }} />
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </Dialog>
    </div>
  );
}

export { QuestionPreview };
